/**
 * Authentication Store - Simplified for single user
 * Clean and focused on essential functionality
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, UserSettings } from '../types'
import { apiClient } from '../services/api'

interface AuthState {
  user: User | null
  settings: UserSettings | null
  isAuthenticated: boolean
  isLoading: boolean
  _hasHydrated: boolean

  // Actions
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  loadSettings: () => Promise<void>
  updateSettings: (settings: Partial<UserSettings>) => Promise<void>
  setHasHydrated: (state: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      settings: null,
      isAuthenticated: false,
      isLoading: false,
      _hasHydrated: false,

      setHasHydrated: (state: boolean) => {
        set({ _hasHydrated: state })
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true })
        try {
          const user = await apiClient.login(email, password)
          set({ user, isAuthenticated: true, isLoading: false })
          await get().loadSettings()
        } catch (error: any) {
          set({ isLoading: false })
          throw error
        }
      },

      logout: async () => {
        set({ isLoading: true })
        try {
          await apiClient.logout()
        } catch {
          // Server session may already be gone — still log out locally
        } finally {
          apiClient.clearSession()
          set({
            user: null,
            settings: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      loadSettings: async () => {
        try {
          const settings = await apiClient.getUserSettings()
          set({ settings })
        } catch (error) {
          console.error('Failed to load settings:', error)
        }
      },

      updateSettings: async (updates: Partial<UserSettings>) => {
        try {
          const settings = await apiClient.updatePreferences(updates)
          set({ settings })
        } catch (error: any) {
          throw error
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        settings: state.settings,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => {
        console.log('[AuthStore] Starting rehydration...')
        return (state, error) => {
          if (error) {
            console.error('[AuthStore] Rehydration error:', error)
            // Mark as hydrated even on error to prevent infinite loading
            if (state) {
              state._hasHydrated = true
              state.isLoading = false
            }
            return
          }
          
          if (state) {
            // CRITICAL: Always set isLoading to false and mark as hydrated
            state.isLoading = false
            state._hasHydrated = true
            console.log('[AuthStore] Rehydrated, isAuthenticated:', state.isAuthenticated)
            
            // Load session from localStorage when store rehydrates
            if (state.isAuthenticated) {
              try {
                console.log('[AuthStore] Loading session from localStorage...')
                apiClient.loadSession()
                console.log('[AuthStore] Session loaded successfully')
                
                // Verify session is actually valid by checking if sessionId exists
                const sessionId = localStorage.getItem('sessionId')
                if (!sessionId) {
                  console.warn('[AuthStore] No sessionId found, clearing auth state')
                  state.isAuthenticated = false
                  state.user = null
                  state.settings = null
                }
              } catch (error) {
                console.error('[AuthStore] Failed to load session:', error)
                // If session load fails, clear auth state silently
                // Don't trigger any redirects here - let ProtectedRoute handle it
                state.isAuthenticated = false
                state.user = null
                state.settings = null
                console.log('[AuthStore] Cleared auth state due to session load failure')
              }
            }
            console.log('[AuthStore] Rehydration complete')
          }
        }
      },
    }
  )
)
